import { DASHBOARD } from "@constants/internal-route.constant";
import { useBranding } from "@context/Branding";
import { ENV } from "config/ENV.config";
import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { readAccess } from "routes/access";
import { isActive, NavItem, visibleNav } from "routes/nav.config";

/** The app name beside the logo in the sidebar's brand strip. */
const APP_NAME = "Portal";

const AppSidebar = () => {
  const { pathname } = useLocation();
  const { logoUrl, sidebarTheme } = useBranding();
  // A company logo whose file is gone (moved bucket, copied data) falls back to the product logo
  // instead of leaving a broken image in the brand slot.
  const [logoFailed, setLogoFailed] = useState(false);
  useEffect(() => setLogoFailed(false), [logoUrl]);
  // Read at render time: a client-side login must be reflected without a reload.
  const items = visibleNav(readAccess());

  const leaf = (item: NavItem) => (
    <li className="nav-item" key={item.to}>
      <NavLink to={item.to!} className={() => `nav-link${isActive(item, pathname) ? " active" : ""}`}>
        <i className={`nav-icon bi ${item.icon}`} />
        <p>{item.label}</p>
      </NavLink>
    </li>
  );

  return (
    <aside className="app-sidebar bg-body-secondary shadow" data-bs-theme={sidebarTheme}>
      {/* The brand strip continues the header (its colours, not the sidebar's): logo | app name. */}
      <div className="sidebar-brand">
        <Link to={DASHBOARD} className="brand-link erp-brand-lockup">
          <img
            src={logoUrl && !logoFailed ? logoUrl : ENV.brandLogo}
            alt={ENV.brandName}
            className="brand-image erp-brand-logo"
            onError={() => logoUrl && setLogoFailed(true)}
          />
          <span className="brand-text">{APP_NAME}</span>
        </Link>
      </div>
      <div className="sidebar-wrapper">
        <nav className="mt-2">
          <ul className="nav sidebar-menu flex-column" role="navigation" aria-label="Main navigation">
            {items.map((item) => {
              if (!item.children) return leaf(item);
              // A group is always open: its row is a label (no toggle, no arrow) over its indented
              // items. The menu is short, and Access is the one most used.
              return (
                // No `menu-open`: AdminLTE paints an open group's row as a highlighted item.
                <li className="nav-item" key={item.label}>
                  <div className="nav-link erp-nav-group">
                    <i className={`nav-icon bi ${item.icon}`} />
                    <p>{item.label}</p>
                  </div>
                  {/* Display is set inline: AdminLTE's start-up pass writes an inline display onto
                      treeviews, which a class alone cannot override. */}
                  <ul className="nav nav-treeview erp-nav-children" style={{ display: "block" }}>
                    {item.children.map(leaf)}
                  </ul>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
    </aside>
  );
};

export default AppSidebar;
